import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Github, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { TokenBuddyLogo } from "./TokenBuddyLogo";

/**
 * Top navigation for the marketing site.
 *
 * Design notes (DESIGN.md + ui-ux-pro-max):
 * - Sticky, calm, hairline-bordered, transparent canvas-tinted with backdrop-blur.
 * - Desktop: inline links, locale switcher, GitHub.
 * - Mobile (< md): GitHub + LocaleSwitcher stay visible, sections collapse behind
 *   a hamburger that opens a full-width panel anchored to the header.
 * - All interactive targets are >= 44px on mobile (44pt Apple HIG / 48dp MD).
 * - Active section is indicated by a small purple signal under the link
 *   (DESIGN.md: "Active routes use a small purple signal, not a filled tab").
 * - Hash links (#pain etc.) are scroll-spy'd via IntersectionObserver.
 *   Route links (/docs) are active when the current pathname matches.
 */

type NavItem =
  | { kind: "hash"; key: string }
  | { kind: "path"; key: string };

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { kind: "hash", key: "install" },
  { kind: "path", key: "docs" },
];

export function TopNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // The /docs route is "active" when the current pathname starts with /docs.
  // Hash links are only active when the user is on the landing page and the
  // scroll-spy has flagged a matching section.
  const onDocs = location.pathname.startsWith("/docs");
  const onHome = location.pathname === "/";

  // Close the mobile panel on Escape, and on any nav click.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Close the mobile panel on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  // Scroll-spy: highlight the section currently dominating the viewport.
  // Only runs on the home page and only tracks hash-kind items.
  useEffect(() => {
    if (!onHome) {
      setActiveSection(null);
      return;
    }
    const hashIds = NAV_ITEMS.filter(
      (i): i is Extract<NavItem, { kind: "hash" }> => i.kind === "hash",
    ).map((i) => i.key);
    const sections = hashIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        // Treat the top 30% of the viewport as the "active" band so the
        // signal flips as soon as a section's heading scrolls into focus.
        rootMargin: "-30% 0px -60% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    for (const section of sections) observer.observe(section);
    return () => observer.disconnect();
  }, [onHome]);

  const isItemActive = (item: NavItem): boolean => {
    if (item.kind === "path") return onDocs;
    return onHome && activeSection === item.key;
  };

  return (
    <header className="sticky top-0 z-30 h-14 border-b border-[var(--color-line)] bg-[var(--color-canvas)]/86 backdrop-blur-xl">
      <div className="page-container flex h-full items-center gap-3">
        <Link
          to="/"
          className="flex min-w-0 items-center rounded-[var(--radius-md)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          aria-label={`${t("meta.siteName")} home`}
        >
          <TokenBuddyLogo />
        </Link>

        <nav
          className="ml-auto hidden items-center gap-1 md:flex"
          aria-label={t("nav.sectionsLabel")}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = isItemActive(item);
            const href = item.kind === "hash" ? `/#${item.key}` : `/${item.key}`;
            return (
              <a
                key={item.key}
                href={href}
                aria-current={isActive ? "location" : undefined}
                className={[
                  "relative inline-flex min-h-9 items-center rounded-[var(--radius-md)] px-2.5 py-1.5 text-[12.5px] font-bold transition-colors",
                  isActive
                    ? "text-[var(--color-ink)]"
                    : "text-[var(--color-muted)] hover:bg-[var(--color-panel-subtle)] hover:text-[var(--color-ink)]",
                ].join(" ")}
              >
                {t(`nav.sections.${item.key}`)}
                <span
                  aria-hidden="true"
                  className={[
                    "absolute inset-x-2.5 -bottom-[6px] h-[2px] rounded-full transition-opacity",
                    isActive ? "bg-[var(--color-primary)] opacity-100" : "opacity-0",
                  ].join(" ")}
                />
              </a>
            );
          })}
        </nav>

        <div className="ml-auto flex min-w-0 items-center gap-2 md:ml-0 md:gap-3">
          <LocaleSwitcher />
          <a
            href="https://github.com/zhouchangui/tokenbuddy-node"
            className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] text-[var(--color-ink-mid)] transition-colors hover:bg-[var(--color-panel-subtle)] md:h-9 md:w-9"
            aria-label={t("nav.github")}
          >
            <Github className="h-4 w-4 md:h-3.5 md:w-3.5" aria-hidden="true" />
          </a>

          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel)] text-[var(--color-ink-mid)] transition-colors hover:bg-[var(--color-panel-subtle)] md:hidden"
            aria-label={t(open ? "nav.closeMenu" : "nav.openMenu")}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? (
              <X className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Menu className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {open ? (
        <div
          id="mobile-menu"
          className="md:hidden"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <nav
            aria-label={t("nav.sectionsLabel")}
            className="border-t border-[var(--color-line)] bg-[var(--color-canvas)] shadow-[0_8px_24px_rgba(60,41,112,0.10)]"
          >
            <ul className="page-container flex flex-col gap-1 py-2">
              {NAV_ITEMS.map((item) => {
                const isActive = isItemActive(item);
                const href = item.kind === "hash" ? `/#${item.key}` : `/${item.key}`;
                return (
                  <li key={item.key}>
                    <a
                      href={href}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "flex min-h-11 items-center justify-between rounded-[var(--radius-md)] px-3 text-[14px] font-bold transition-colors",
                        isActive
                          ? "bg-[var(--color-primary-soft)] text-[var(--color-primary-deep)]"
                          : "text-[var(--color-ink-mid)] hover:bg-[var(--color-panel-subtle)]",
                      ].join(" ")}
                    >
                      <span>{t(`nav.sections.${item.key}`)}</span>
                      {isActive ? (
                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 rounded-full bg-[var(--color-primary)]"
                        />
                      ) : null}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
